import { getPortalSdk } from 'portal-sdk';
import { v4 as uuid } from 'uuid';

import { DEFAULT_PROJECT_PROPERTY_TYPE } from 'src/constants/project';
import { CompetencyQuestion } from 'src/constants/voicebox';
import { createEmptyDiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  getDefaultProjectDataPrefix,
  getDefaultProjectModelPrefix,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { CURRENT_MIGRATION_VERSION } from 'src/reducers/migrations/constants';
import { Project, createEmptyPublishHistory } from 'src/store/state/projects';
import { encodeCloudContent } from 'src/utils/exportProject/encodeProject';
import { getCurrentOrgDomain } from 'src/utils/getCurrentOrgDomain';
import { getPrefixFromNamespace } from 'src/utils/getPrefixFromNamespace';
import { getCloudProjectListQueryKey } from 'src/utils/hooks/useCloudStorageProjects';
import { queryClient } from 'src/utils/queryClient';

/**
 * Creates a new cloud project with the given name
 *
 * @param name The name of the project
 * @returns The ID of the newly created cloud project, or null if creation failed
 */
export const createNewCloudProject = async (
  {
    competencyQuestions,
    ...projectData
  }: Pick<Project, 'name' | 'description'> & {
    competencyQuestions?: CompetencyQuestion[];
  },
  connectionId: string | undefined
): Promise<string | null> => {
  const portalSdk = getPortalSdk();

  if (!portalSdk) {
    console.error('Portal SDK not available');
    return null;
  }

  // Create a new project object with the necessary structure
  const id = uuid();
  const modelId = uuid();
  const dateCreated = Date.now();
  const orgDomain = getCurrentOrgDomain();

  // Create a new diagram object
  const diagram = createEmptyDiagramSnapshot();
  diagram.model.modelData.competencyQuestions = [
    ...(competencyQuestions || []),
  ];

  // Create a new project object
  const project: Project = {
    ...projectData,
    id,
    connectionId,
    orgDomain,
    dateCreated,
    dateLastUpdated: dateCreated,
    isCloudProject: true,
    isRemoved: false,
    diagram,
    fileUploadsById: {},
    virtualGraphsById: {},
    modelsById: {
      [modelId]: {
        classes: null,
        colorIdsByIri: null,
        connectionId: null,
        databaseId: null,
        databaseNamespaces: null,
        dateLastUpdated: dateCreated,
        descriptionsByIri: null,
        geoDataByIri: null,
        id: modelId,
        labelsByIri: null,
        name: projectData.name,
        namespace: getDefaultProjectModelPrefix({ name: projectData.name }),
        prefix: getPrefixFromNamespace(
          getDefaultProjectModelPrefix({ name: projectData.name })
        ),
        properties: null,
        rules: null,
        shouldPublishPrefix: true,
      },
    },
    dataNamespace: '',
    propertyType: DEFAULT_PROJECT_PROPERTY_TYPE,
    publishHistory: createEmptyPublishHistory(),
    targetModelId: modelId,
  };
  project.dataNamespace = getDefaultProjectDataPrefix(project);
  // Encode the project for cloud storage
  const encodedProject = encodeCloudContent(project, CURRENT_MIGRATION_VERSION);

  if (!encodedProject) {
    console.error('Failed to encode project for cloud storage');
    return null;
  }

  // Create the project in the cloud
  const cloudProjectId = await portalSdk.createDesignerProject(
    projectData.name,
    encodedProject,
    connectionId,
    orgDomain
  );

  if (cloudProjectId) {
    // Refetch the cloud project list
    queryClient.refetchQueries(getCloudProjectListQueryKey());

    return cloudProjectId;
  }

  return null;
};
